'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useAuthContext } from '@/contexts/AuthContext'
import { isStandalone } from '@/lib/utils/pwa'
import { loginWithGoogleOneTap } from '@/lib/utils/googleAuth'

// Paths donde NO se debe mostrar el One Tap. Coincide con la lista de rutas
// "privadas" de la app. El login y el signup mantienen su propio botón
// "Continuar con Google" pero no necesitan la card flotante de One Tap.
const ONETAP_EXCLUDED_SEGMENTS = [
  'onboarding',
  'login',
  'signup',
  'dashboard',
  'admin',
  'booking',
  'manage-booking',
  'verify-email',
  'menu',
  'cartas'
]

const isExcludedPath = (pathname: string): boolean => {
  const segments = pathname.split('/').filter(Boolean)
  // Quitar el primer segmento de locale (es / en).
  const pathSegments = segments.slice(1)
  return pathSegments.some((segment) => ONETAP_EXCLUDED_SEGMENTS.includes(segment))
}

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID

interface GoogleAccountsId {
  initialize: (config: Record<string, unknown>) => void
  prompt: (callback?: (notification: unknown) => void) => void
  cancel: () => void
  disableAutoSelect: () => void
}

interface GoogleAccountsGlobal {
  accounts: { id: GoogleAccountsId }
}

const getGoogleAccounts = (): GoogleAccountsGlobal | undefined => {
  if (typeof window === 'undefined') return undefined
  return (window as unknown as { google?: GoogleAccountsGlobal }).google
}

const DISMISSED_REASONS_TO_RESPECT = new Set([
  'user_cancel',
  'credential_rejected',
  'tap_outside'
])

const GoogleOneTap = () => {
  const pathname = usePathname()
  const { user } = useAuthContext()
  const [gisReady, setGisReady] = useState(() => Boolean(getGoogleAccounts()?.accounts?.id))
  const promptTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Escucha el evento `gis-loaded` que dispara `GoogleScriptLoader` cuando
  // el script de Google Identity Services termina de cargar. Sin esto, el
  // useEffect de inicialización se ejecuta antes de que `window.google`
  // exista y falla silenciosamente.
  useEffect(() => {
    const onGisLoaded = () => setGisReady(true)
    window.addEventListener('gis-loaded', onGisLoaded)
    return () => window.removeEventListener('gis-loaded', onGisLoaded)
  }, [])

  useEffect(() => {
    // No mostrar si no hay client ID configurado (entorno sin GIS).
    if (!GOOGLE_CLIENT_ID) return
    // No mostrar en PWA standalone: Google suprime One Tap cuando la app
    // ya está instalada (es política de Google), pero el `prompt()` igualmente
    // cuenta contra la rate-limit por origen y ensucia la consola con warnings.
    if (isStandalone()) return
    // No mostrar en páginas excluidas.
    if (isExcludedPath(pathname)) return
    // No mostrar si el usuario ya está autenticado.
    if (user) return
    // Esperar a que el script de GIS haya cargado.
    if (!gisReady) return
    // No mostrar si el usuario ya cerró el prompt en esta sesión.
    if (typeof window === 'undefined') return
    if (window.sessionStorage.getItem('googleOneTapDismissed') === '1') return

    const gis = getGoogleAccounts()
    if (!gis?.accounts?.id) return

    const handleCredential = (response: { credential?: string }) => {
      if (!response.credential) return
      loginWithGoogleOneTap(response.credential, 'onetap')
        .catch((err) => console.error('Google One Tap credential error', err))
    }

    gis.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredential,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: true,
      auto_select: false,
      itp_support: true
    })

    // Mostrar el prompt tras un pequeño delay para no interferir con el LCP
    // de la landing.
    promptTimer.current = setTimeout(() => {
      try {
        gis.accounts.id.prompt((notification: any) => {
          // Solo guardamos el dismissal cuando ha sido una acción explícita
          // del usuario. NO guardamos cuando el prompt se salta por motivos
          // del sistema ('skipped' = rate limit, 'unknown' = error interno,
          // 'moment === dismissed' demasiado amplio y puede incluir fallos
          // de FedCM). Si guardamos esos, el prompt deja de aparecer en
          // sesiones futuras sin que el usuario lo haya rechazado realmente.
          const reason = notification?.getDismissedReason?.()
          if (reason && DISMISSED_REASONS_TO_RESPECT.has(reason)) {
            window.sessionStorage.setItem('googleOneTapDismissed', '1')
          }
        })
      } catch {
        // GIS puede lanzar en navegadores sin soporte. Lo silenciamos.
      }
    }, 1200)

    return () => {
      if (promptTimer.current) clearTimeout(promptTimer.current)
    }
  }, [gisReady, pathname, user])

  return null
}

export default GoogleOneTap
